/**
 * Mobile Time Picker Handlers Hook
 * Extracted from CreateTransactionMobile.tsx
 * 
 * Handles time picker done button logic
 */

import { useCallback } from "react";
import { TransactionCustomFieldValues } from "../../../../types";

// Local types matching CreateTransactionMobile.tsx
interface FormData {
  category: string;
  amount: string;
  currency: string;
  rate: string;
  defaultCurrencyRate: string;
  date: string;
  time: string;
  accountId: string;
  accountGroup: string;
  details?: any;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
}

interface TransferFormData {
  fromAccountId: string;
  toAccountId: string;
  amount: string;
  date: string;
  time: string;
  transferRate: string;
  feeAmount?: string;
  feeAccountId?: string;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
}

export type FocusedField = "amount" | "date" | "time" | "account" | "currency" | "category" | "fromAccount" | "toAccount" | "rate" | "defaultCurrencyRate" | "feeAccount" | "feeAmount" | null;

export interface UseMobileTimePickerHandlersProps {
  transactionType: "expense" | "income" | "transfer";
  formData: FormData;
  transferFormData: TransferFormData;
  setFocusedField: (field: FocusedField) => void;
  setKeepTimePickerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isOnOptionalField: boolean;
  handleFieldFocus: (field: FocusedField) => void;
}

export function useMobileTimePickerHandlers({
  transactionType,
  formData,
  transferFormData,
  setFocusedField,
  setKeepTimePickerOpen,
  isOnOptionalField,
  handleFieldFocus,
}: UseMobileTimePickerHandlersProps) {
  const handleTimePickerDone = useCallback(() => {
    // Close time picker
    setFocusedField(null);
    setKeepTimePickerOpen(false);
    
    // After closing, focus on the next empty required field
    // BUT only if user is not currently on optional fields (note/attachment)
    setTimeout(() => {
      // Don't auto-focus if user is on optional fields
      if (isOnOptionalField) {
        return;
      }
      
      if (transactionType === "transfer") {
        // For transfers: amount -> fromAccount -> toAccount -> date
        const currentAmount = transferFormData.amount;
        if (!currentAmount || currentAmount.trim() === "") {
          handleFieldFocus("amount");
        } else if (!transferFormData.fromAccountId || transferFormData.fromAccountId.trim() === "") {
          handleFieldFocus("fromAccount");
        } else if (!transferFormData.toAccountId || transferFormData.toAccountId.trim() === "") {
          handleFieldFocus("toAccount");
        } else if (!transferFormData.date || transferFormData.date.trim() === "") {
          handleFieldFocus("date");
        } else {
          // All required fields are filled, don't focus on anything
          setFocusedField(null);
        }
      } else {
        // For expense/income: amount -> account -> category -> date
        const currentAmount = formData.amount;
        if (!currentAmount || currentAmount.trim() === "") {
          handleFieldFocus("amount");
        } else if (!formData.accountId || formData.accountId.trim() === "") {
          handleFieldFocus("account");
        } else if (!formData.category || formData.category.trim() === "") {
          handleFieldFocus("category");
        } else if (!formData.date || formData.date.trim() === "") {
          handleFieldFocus("date");
        } else {
          // All required fields are filled, don't focus on anything
          setFocusedField(null);
        }
      }
    }, 100);
  }, [
    transactionType,
    formData,
    transferFormData,
    setFocusedField,
    setKeepTimePickerOpen,
    isOnOptionalField,
    handleFieldFocus,
  ]);

  return {
    handleTimePickerDone,
  };
}
